import React, { createContext, useState, useContext, ReactNode } from 'react';

export type TourPage = 'dashboard' | 'reports' | 'ai-solutions';

interface TourContextProps {
  runDashboardTour: boolean;
  runReportsTour: boolean;
  runAISolutionsTour: boolean;
  startPageTour: (page: TourPage) => void;
  stopPageTour: (page: TourPage) => void;
  isPageTourSeen: (page: TourPage) => boolean;
  resetPageTour: (page: TourPage) => void;
}

const TourContext = createContext<TourContextProps | undefined>(undefined);

export const TourProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [runDashboardTour, setRunDashboardTour] = useState(false);
  const [runReportsTour, setRunReportsTour] = useState(false);
  const [runAISolutionsTour, setRunAISolutionsTour] = useState(false);

  const isPageTourSeen = (page: TourPage): boolean => {
    return localStorage.getItem(`tour-${page}-seen`) === 'true';
  };

  const startPageTour = (page: TourPage) => {
    switch (page) {
      case 'dashboard':
        setRunDashboardTour(true);
        break;
      case 'reports':
        setRunReportsTour(true);
        break;
      case 'ai-solutions':
        setRunAISolutionsTour(true);
        break;
    }
  };

  const stopPageTour = (page: TourPage) => {
    localStorage.setItem(`tour-${page}-seen`, 'true');
    switch (page) {
      case 'dashboard':
        setRunDashboardTour(false);
        break;
      case 'reports':
        setRunReportsTour(false);
        break;
      case 'ai-solutions':
        setRunAISolutionsTour(false);
        break;
    }
  };

  const resetPageTour = (page: TourPage) => {
    localStorage.removeItem(`tour-${page}-seen`);
    switch (page) {
      case 'dashboard':
        setRunDashboardTour(false);
        break;
      case 'reports':
        setRunReportsTour(false);
        break;
      case 'ai-solutions':
        setRunAISolutionsTour(false);
        break;
    }
  };

  return (
    <TourContext.Provider value={{ 
      runDashboardTour, 
      runReportsTour, 
      runAISolutionsTour, 
      startPageTour, 
      stopPageTour, 
      isPageTourSeen,
      resetPageTour
    }}>
      {children}
    </TourContext.Provider>
  );
};

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};
